import { useEffect } from "react";
import { useAuthState } from "@/store/auth";
import { useGetVisitsQuery } from "@/gqlhooks/visit/useGetVisitsQuery";
import { useScrollRefresh } from "@/components/shared/scroll-content/scroll-refresh-context";
import { addDays, addMonths } from "date-fns";
import { formatDate } from "@/lib/helpers/date";
import {
  VisitStatusEnum,
  SortEnum,
  VisitSortByEnum,
} from "@/__generated__/graphql";
const VISIT_PER_PAGE = 20;
export const useUpcomingVisit = () => {
  const clientId = useAuthState((state) => state.data.userInfo.id);
  const { refreshing, setRefreshing } = useScrollRefresh();
  // Need this policy to make refreshing work
  const next7daysQuery = useGetVisitsQuery({
    skip: !clientId,
    fetchPolicy: "cache-and-network",
    variables: {
      filters: {
        clientId: [clientId],
        from: formatDate(new Date()),
        to: formatDate(addDays(new Date(), 6)),
        sortBy: [VisitSortByEnum.ServiceDate],
        sort: [SortEnum.Asc],
      },
    },
  });
  const next30daysQuery = useGetVisitsQuery({
    skip: !clientId,
    fetchPolicy: "cache-and-network",
    variables: {
      filters: {
        clientId: [clientId],
        from: formatDate(addDays(new Date(), 7)),
        to: formatDate(addMonths(new Date(), 1)),
        offset: 0,
        limit: VISIT_PER_PAGE,
        sortBy: [VisitSortByEnum.ServiceDate],
        sort: [SortEnum.Asc],
      },
    },
  });
  const unscheduledQuery = useGetVisitsQuery({
    skip: !clientId,
    fetchPolicy: "cache-and-network",
    variables: {
      filters: {
        clientId: [clientId],
        from: formatDate(new Date()),
        to: formatDate(addMonths(new Date(), 1)),
        status: [
          VisitStatusEnum.PendingClientSchedule,
          VisitStatusEnum.UnableToSchedule,
        ],
        sortBy: [VisitSortByEnum.ServiceDate],
        sort: [SortEnum.Asc],
      },
    },
  });
  useEffect(() => {
    if (refreshing) {
      Promise.all([
        next7daysQuery.refetch(),
        next30daysQuery.refetch(),
        unscheduledQuery.refetch(),
      ]).then(() => {
        setRefreshing(false);
      });
    }
  }, [refreshing]);
  const is7DaysEmpty =
    next7daysQuery.list.length === 0 && !next7daysQuery.loading;
  const is30DaysEmpty =
    next30daysQuery.list.length === 0 && !next30daysQuery.loading;
  const isUnscheduledEmpty =
    unscheduledQuery.list.length === 0 && !unscheduledQuery.loading;
  const isEmpty = is7DaysEmpty && is30DaysEmpty && isUnscheduledEmpty;
  return {
    isEmpty,
    isUnscheduledEmpty,
    next7daysQuery,
    next30daysQuery,
    unscheduledQuery,
  };
};
